import { AppError, ErrorContext } from '@nvgc/shared';

export interface ErrorReport {
  error: AppError;
  context: ErrorContext;
  userAgent: string;
  url: string;
  timestamp: Date;
}

class ErrorReportingService {
  private reports: ErrorReport[] = [];
  private maxReports = 100;

  /**
   * Report an error for debugging and monitoring
   */
  reportError(error: AppError, context: Partial<ErrorContext> = {}): void {
    const report: ErrorReport = {
      error,
      context: {
        ...context,
        userAgent: navigator.userAgent,
        url: window.location.href
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date()
    };

    // Store locally for debugging
    this.reports.unshift(report);
    if (this.reports.length > this.maxReports) {
      this.reports = this.reports.slice(0, this.maxReports);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Error Report: ${error.code}`);
      console.error('Error:', error);
      console.log('Context:', context);
      console.log('Stack:', error.stack);
      console.groupEnd();
    }

    // In production, you would send this to your monitoring service
    // Example: Sentry, LogRocket, or custom endpoint
    if (process.env.NODE_ENV === 'production' && error.severity === 'critical') {
      this.sendToMonitoringService(report);
    }
  }

  /**
   * Get recent error reports for debugging
   */
  getRecentReports(limit = 10): ErrorReport[] {
    return this.reports.slice(0, limit);
  }

  /**
   * Clear stored error reports
   */
  clearReports(): void {
    this.reports = [];
  }

  /**
   * Send error report to monitoring service
   */
  private async sendToMonitoringService(report: ErrorReport): Promise<void> {
    try {
      // In a real implementation, you would send to your monitoring service
      // For now, we'll just log it
      console.warn('Would send error report to monitoring service:', report);
      
      // Example implementation:
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(report)
      // });
    } catch (sendError) {
      console.error('Failed to send error report:', sendError);
    }
  }

  /**
   * Create error context from current application state
   */
  createContext(component?: string, action?: string, metadata?: Record<string, unknown>): ErrorContext {
    return {
      component,
      action,
      metadata,
      url: window.location.href,
      userAgent: navigator.userAgent,
      sessionId: this.getSessionId(),
      requestId: this.generateRequestId()
    };
  }

  private getSessionId(): string {
    // Get session ID from localStorage or generate one
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = this.generateRequestId();
      localStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

export const errorReportingService = new ErrorReportingService();